/**
 * THE ONE phase catalogue for the acquisition journey, shared by both licence
 * views (WorkplanView's Timeline and ProjectDashboard's List).
 *
 * History: each view carried its own hardcoded copy and they drifted — the List
 * copy was missing 20 of the 30 NG corpus ids, and the Timeline had NO fallback
 * bucket, so a licence id in no phase array silently VANISHED from the workplan
 * (found 2026-07-15 while planning dataset v1, which adds new ids). One config,
 * one partition function, and an explicit "unphased" remainder kill that class:
 * a new id can be mis-phased (visible, fixable) but never invisible.
 *
 * Phase placement is presentation, not law: a licence's binding force comes from
 * the corpus (applies_when / applicability_status), never from these arrays.
 */

export interface WorkplanPhaseDef {
  label: string;
  ids: string[];
  desc: string;
  unlock?: string;
}

export const WORKPLAN_PHASES: Record<string, WorkplanPhaseDef[]> = {
  KE: [
    { label: "Phase 1: Pre-development", ids: ["KE-L04", "KE-L11"], desc: "Secure land + company", unlock: "Land control established — unlocks Phase 2" },
    { label: "Phase 2: Environmental + Grid", ids: ["KE-L02", "KE-L03", "KE-L05", "KE-L04B"], desc: "Parallel regulatory streams", unlock: "EIA + grid study complete — unlocks Phase 3" },
    { label: "Phase 3: Licensing + Equipment", ids: ["KE-L01", "KE-L06", "KE-L07", "KE-L08", "KE-L09"], desc: "Core license applications", unlock: "Licenses granted — unlocks Phase 4" },
    { label: "Phase 4: Construction + COD", ids: ["KE-L10", "KE-L12"], desc: "Build and commission" },
  ],
  NG: [
    { label: "Phase 1: Pre-development", ids: [
      "ng-commission-shall-register-memorandumarticles-unless-non-comp",
      "ng-every-taxable-person-shall-register-with-relevant-tax-author",
      "ng-relevant-tax-authority-shall-issue-tax-clearance-certificate",
      "ng-governor-may-grant-statutory-rights-of-occupancy-to-any-pers",
      "ng-local-government-may-grant-customary-rights-of-occupancy-to",
      "ng-governor-may-issue-certificate-of-occupancy-in-evidence-of-s",
      "ng-alienation-assignment-mortgage-transfer-sublease-bequest-of",
      "ng-alienation-of-customary-right-of-occupancy-requires-governor",
      "NG-L11",
    ], desc: "Company, tax + land", unlock: "Company + land secured — unlocks Phase 2" },
    { label: "Phase 2: Environmental + Grid", ids: [
      "NG-L03",
      "ng-mini-grid-environmental-esmp-esia",
      "ng-a-person-involved-in-battery-storage-shall-register-with-a-p",
      "ng-persons-wishing-to-connect-to-and-use-the-network-must-acced",
      "ng-the-grid-code-and-the-grid-connection-agreement-together-wit",
      "ng-mini-grid-system-impact-study",
      "ng-for-an-application-to-be-deemed-successful-an-average-of-80",
    ], desc: "EIA/ESMP, battery EPR + grid connection", unlock: "Environmental + grid complete — unlocks Phase 3" },
    { label: "Phase 3: Licensing + Equipment", ids: [
      "ng-generation-license-issued-under-s-65",
      "ng-distribution-license-issued-under-s-68",
      "ng-embedded-generation-licence",
      "ng-nerc-may-grant-permit-for-isolated-mini-grid-where-applicant",
      "ng-mini-grid-registration-under-100kw",
      "ng-nerc-may-issue-permit-pursuant-to-s-7-or-register-tripartite",
      "ng-mini-grid-tripartite-agreement-registration",
      "ng-net-billing-registration",
      "ng-community-and-distribution-licensee-may-grant-mini-grid-deve",
      "ng-notwithstanding-default-benchmark-commission-may-approve-pro",
      "ng-commission-permission-for-higher-feed-in-tariff-s-1693",
      "ng-soncap-certificate",
      "NG-L02B", "NG-L06", "NG-L10",
      "ng-an-application-for-economic-development-incentive-certificat",
      "ng-relevant-tax-authority-may-issue-advance-ruling-within-21-da",
    ], desc: "NERC licensing + equipment", unlock: "Licensed — unlocks Phase 4" },
    { label: "Phase 4: Construction + COD", ids: [
      "ng-only-skilled-experienced-and-competent-electrical-installati",
      "ng-meter-certification-issued-by-accredited-meter-test-station",
      "ng-net-billing-nemsa-inspection",
      "ng-net-billing-commissioning-approval",
      "ng-mini-grid-energisation-approval",
      "NG-L09", "NG-L12",
      "ng-license-renewal-under-s-73",
      "ng-license-amendment-under-s-74",
      "ng-mini-grid-transfer-consent",
      "ng-where-a-mini-grid-registered-or-permitted-under-regulations",
      "ng-re-registration-certificate-private-to-public",
      "ng-public-company-limited-by-shares-may-re-register-as-unlimite",
      "ng-foreign-company-exemption-from-cama-registration-specific-fe",
      "ng-whenever-relevant-tax-authority-of-opinion-that-tax-assessed",
    ], desc: "Build, certify + operate" },
  ],
  ZA: [
    { label: "Phase 1: Pre-development", ids: ["ZA-L04", "ZA-L04B", "ZA-L10"], desc: "Land + rezoning", unlock: "Land rights established — unlocks Phase 2" },
    { label: "Phase 2: Environmental + Grid + Heritage", ids: ["ZA-L02", "ZA-L02B", "ZA-L03", "ZA-L05", "ZA-L06", "ZA-L07"], desc: "Parallel environmental streams", unlock: "EA + grid access — unlocks Phase 3" },
    { label: "Phase 3: Registration + Licensing", ids: ["ZA-L01", "ZA-L01B", "ZA-L11"], desc: "NERSA registration", unlock: "Registered — unlocks Phase 4" },
    { label: "Phase 4: Construction + COD", ids: ["ZA-L08", "ZA-L09"], desc: "Build and commission" },
  ],
  GH: [
    { label: "Phase 1: Pre-development", ids: ["GH-L04", "GH-L07", "GH-L09"], desc: "Land + business permit + tax exemption", unlock: "Land secured — unlocks Phase 2" },
    { label: "Phase 2: Environmental + Grid", ids: ["GH-L02", "GH-L03", "GH-L11", "GH-L12"], desc: "EPA + GRIDCo + utilities", unlock: "EPA permit + grid access — unlocks Phase 3" },
    { label: "Phase 3: Licensing + Equipment", ids: ["GH-L01", "GH-L05", "GH-L06", "GH-L08", "GH-L10"], desc: "Energy Commission + PURC + building", unlock: "Licensed — unlocks Phase 4" },
    { label: "Phase 4: Construction + COD", ids: [], desc: "Build and commission" },
  ],
  NA: [
    { label: "Phase 1: Pre-development", ids: ["NA-L04", "NA-L05"], desc: "Planning consent + land", unlock: "Land secured — unlocks Phase 2" },
    { label: "Phase 2: Environmental + Grid", ids: ["NA-L02", "NA-L03"], desc: "ECC + NamPower", unlock: "Clearance + grid — unlocks Phase 3" },
    { label: "Phase 3: Licensing + Equipment", ids: ["NA-L01", "NA-L06", "NA-L07", "NA-L08"], desc: "ECB licence + building", unlock: "Licensed — unlocks Phase 4" },
    { label: "Phase 4: Construction + COD", ids: [], desc: "Build and commission" },
  ],
  ZM: [
    { label: "Phase 1: Pre-development", ids: ["ZM-L04", "ZM-L05"], desc: "Land leasehold + community consent", unlock: "Land secured — unlocks Phase 2" },
    { label: "Phase 2: Environmental + Grid", ids: ["ZM-L02", "ZM-L03"], desc: "ZEMA + ZESCO", unlock: "EIA + grid — unlocks Phase 3" },
    { label: "Phase 3: Licensing + Equipment", ids: ["ZM-L01", "ZM-L06", "ZM-L07", "ZM-L08"], desc: "ERB licence + building", unlock: "Licensed — unlocks Phase 4" },
    { label: "Phase 4: Construction + COD", ids: [], desc: "Build and commission" },
  ],
};

/** The fallback phase label — rendered for any licence in no phase array. */
export const UNPHASED_LABEL = "Other approvals (not yet phased)";

/**
 * Partition licences into the country's phases plus an explicit remainder.
 * `other` is the load-bearing part: it is every licence the arrays don't know,
 * which before 2026-07-15 silently vanished from the Timeline view.
 */
export function partitionByPhase<T extends { license_type_id: string }>(
  countryCode: string,
  licenses: T[],
): { phases: Array<WorkplanPhaseDef & { licenses: T[] }>; other: T[] } {
  const defs = WORKPLAN_PHASES[countryCode] ?? WORKPLAN_PHASES.KE!;
  const phases = defs.map((d) => ({
    ...d,
    licenses: licenses.filter((l) => d.ids.includes(l.license_type_id)),
  }));
  const known = new Set(defs.flatMap((d) => d.ids));
  const other = licenses.filter((l) => !known.has(l.license_type_id));
  return { phases, other };
}
